import { forwardRef } from 'react';
import ReactCurrencyInput, {
  type CurrencyInputProps as ReactCurrencyInputProps,
} from 'react-currency-input-field';

import type { ComponentProps } from '~/chords/ComponentProps';
import { getClassNameOverride } from '~/chords/getClassNameOverride';
import { cn } from '~/chords/lib/cn';
import { variants } from '~/chords/lib/variants';

import type { InputRounded, InputSize, InputState } from '../types';
import { inputVariants } from '../variants';

export type CurrencyInputWidth = 'full' | 'fixed';

export interface CurrencyInputProps
  extends ComponentProps<
    Omit<ReactCurrencyInputProps, 'size' | 'ref' | 'className' | 'width'>
  > {
  size?: InputSize;
  rounded?: InputRounded;
  state?: keyof typeof InputState;
  width?: CurrencyInputWidth;
}

const currencyInputVariants = variants({
  variants: {
    width: {
      full: 'w-full',
      fixed: 'w-[180px]',
    },
  },
});

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      size = 'large',
      rounded = 'xsmall',
      state = 'default',
      width = 'full',
      ...props
    },
    ref,
  ) => {
    const stateClasses = inputVariants({
      state,
      rounded,
      size,
    });

    const className = getClassNameOverride(props, 'CurrencyInput');
    const {
      dangerouslyOverrideClassName: _dangerouslyOverrideClassName,
      ...propsWithoutClassName
    } = props;

    return (
      <ReactCurrencyInput
        aria-invalid={state === 'error'}
        className={cn(
          stateClasses,
          currencyInputVariants({ width }),
          className,
        )}
        ref={ref}
        {...propsWithoutClassName}
      />
    );
  },
);

CurrencyInput.displayName = 'CurrencyInput';
